import { ChoiceButtons } from './IdentityMemory';
import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search } from 'lucide-react';
import { beverageProductsService } from '../services';
import { EvidencePillRow } from './EvidencePillRow';
import { AiCompareStatus, AiVerdictChip } from './AiCompare';

/**
 * Delivery-document line review — explicit full Product Master search.
 *
 * The "Suggest" candidates list above is deliberately capped to the top
 * few name-matching results so it stays readable; this box lets the
 * reviewer search the ENTIRE Product Master (same org-scoped
 * /beverage-products search every other picker in the app uses) and
 * explicitly choose an item that never appeared in — or was outranked
 * out of — that shortlist. Each result also shows the same 5 PSB
 * attribute pills the ranked candidates show (via onLoadPills — see
 * DeliveryDocumentImportPanel), so the auditor can inspect evidence here
 * too, without this box computing a percentage or ranking of its own.
 *
 * Selecting a result calls the exact same onSelect(productId) the
 * ranked-candidate "Select" buttons call, so it goes through the same
 * set_reviewed_product + portfolio "remembered pair" teach/conflict
 * flow already established for this panel. Nothing here creates a
 * product, searches outside the signed-in organisation, or writes
 * anything by itself — only a result's own "Select" click does.
 */
export default function DeliveryLineFullMasterSearch({ lineId, isEditable, savingLineId, onSelect, pillsForLine, onLoadPills, aiState, onRunAi }) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState({ loading: false, results: [], error: null, searched: false });
  const seqRef = useRef(0);
  const timerRef = useRef(null);

  const runSearch = (value) => {
    const q = value.trim();
    if (!q) {
      setState({ loading: false, results: [], error: null, searched: false });
      return;
    }
    const seq = ++seqRef.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    beverageProductsService.list({ search: q, lite: true, page: 1, page_size: 25 })
      .then((data) => {
        if (seq !== seqRef.current) return;
        const results = data.items || [];
        setState({ loading: false, results, error: null, searched: true });
        if (results.length && onLoadPills) onLoadPills(results.map((p) => p.id));
      })
      .catch(() => {
        if (seq !== seqRef.current) return;
        setState({ loading: false, results: [], error: 'Search failed — try again', searched: true });
      });
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(value), 300);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (!isEditable) return null;

  const aiIds = state.results.slice(0, 8).map((p) => p.id);
  const aiFresh = Boolean(aiState && aiState.ids === aiIds.join(','));

  return (
    <div className="border-t pt-2 mt-2 space-y-1.5" data-testid={`ddi-full-search-${lineId}`}>
      <div className="relative">
        <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={handleChange}
          placeholder="Search the full Product Master (name, code, brand)…"
          className="h-7 text-xs pl-7"
          data-testid={`ddi-full-search-input-${lineId}`}
        />
      </div>
      {state.loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Searching Product Master…</div>
      ) : state.error ? (
        <div className="text-xs text-rose-700">{state.error}</div>
      ) : state.searched && state.results.length === 0 ? (
        <div className="text-xs text-muted-foreground">No Product Master items match "{query}".</div>
      ) : state.results.length > 0 ? (
        <div className="space-y-1.5" data-testid={`ddi-full-search-results-${lineId}`}>
          {onRunAi && (
            <AiCompareStatus
              state={aiFresh ? aiState : null}
              onRun={() => onRunAi(aiIds)}
              testIdPrefix={`ddi-ai-search-${lineId}`}
            />
          )}
          {state.results.map((p) => (
            <div key={p.id} className="text-xs bg-white border rounded px-2 py-1" data-testid={`ddi-full-search-result-${lineId}-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium break-words">{p.product_name}{p.brand && <span className="text-muted-foreground font-normal"> ({p.brand})</span>}</div>
                  <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground mt-0.5">
                    <span>Code: {p.product_code || '\u2014'}</span>
                    <span>{p.subcategory || '\u2014'}</span>
                    {p.status !== 'active' && <Badge variant="outline" className="text-[10px]">{p.status}</Badge>}
                  </div>
                </div>
                <ChoiceButtons
                  busy={savingLineId === lineId} testIdPrefix={`ddi-full-search-select-${lineId}-${p.id}`}
                  onIdentity={() => onSelect(p.id, 'identity')} onLineOnly={() => onSelect(p.id, 'line_only')}
                />
              </div>
              {aiFresh && <AiVerdictChip verdict={aiState?.data?.verdicts?.[String(p.id)]} testId={`ddi-ai-search-verdict-${lineId}-${p.id}`} />}
              <EvidencePillRow
                pills={pillsForLine?.[p.id]}
                loading={pillsForLine?.[p.id]?.loading}
                testIdPrefix={`ddi-full-search-pills-${lineId}-${p.id}`}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

